import { useParams, Navigate } from "react-router-dom";
import { AuthView as NeonAuth } from "@neondatabase/neon-js/auth/react";
import { Card } from "@/components/ui/card";
import { Dumbbell } from "lucide-react";

export default function Auth() {
  const { pathname } = useParams<{ pathname: string }>();

  // Redirect to sign-in if pathname is invalid
  if (!pathname || !["sign-in", "sign-up", "forgot-password"].includes(pathname)) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  const getTitle = () => {
    switch (pathname) {
      case "sign-in":
        return { title: "Welcome Back", subtitle: "Sign in to access your training plans" };
      case "sign-up":
        return { title: "Create Account", subtitle: "Get started with your fitness journey" };
      case "forgot-password":
        return { title: "Reset Password", subtitle: "Enter your email to reset your password" };
      default:
        return { title: "", subtitle: "" };
    }
  };

  const { title, subtitle } = getTitle();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-card to-background px-4 sm:px-6 py-12">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative">
        <Card className="p-8 shadow-2xl border-border/50 backdrop-blur-sm bg-card/95">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Dumbbell className="h-6 w-6 text-amber-500" />
            </div>
            <span className="font-bold text-2xl tracking-tight">Bodyz Gym</span>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
              {title}
            </h1>
            <p className="text-muted-foreground mt-2 text-sm">
              {subtitle}
            </p>
          </div>

          {/* Auth Component */}
          <div className="auth-wrapper">
            <NeonAuth view={pathname as any} />
          </div>

          {/* Footer */}
          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              By continuing, you agree to our{" "}
              <a href="/terms-of-service" className="text-amber-500 hover:text-amber-400 transition-colors">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy-policy" className="text-amber-500 hover:text-amber-400 transition-colors">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
